import { inject, Injectable } from '@angular/core';
import { HEADERS } from '../api-headers';
import { HttpClient, HttpParams } from '@angular/common/http';

import {
  UTILS_GET_ALGORITHM_PARAMETERS,
  UTILS_POST_ASSIGN_FACE,
  UTILS_POST_ASSIGN_LOGO,
  UTILS_GET_GET_IMAGE_FACES,
  UTILS_GET_GET_IMAGE_FACES_SUGGEST,
  UTILS_POST_TRANSLATE,
  UTILS_GET_I18N,
  UTILS_GET_I18N_TABLE,
  UTILS_POST_I18N,
  UTILS_GET_SCHEMA,
  UTILS_GET_TIMEZONES,
  UTILS_GET_LANGUAGES,
  UTILS_GET_COVER_PERIODS,
  UTILS_GET_PROFIT_CENTERS,
  UTILS_GET_PARTNER_AGENCIES,
  UTILS_GET_COUNTRIES,
  UTILS_GET_CITIES,
} from '../endpoints/utils.endpoints';

import { toQueryParams } from '../api-utils';

import {
  Face,
  LogoDetection,
  NomenclatureItem,
  NomItem,
} from 'src/app/core/models/core.models';
import { FormProperty } from 'src/app/core/models/form.models';
import { PromptParametersSchema } from './feed-api.service';

import { Observable } from 'rxjs';

export interface TranslationTableRecord {
  key: string;
  en: string;
  bg: string;
  ar: string;
}

export interface ImageFacesResponse {
  faces: Array<Face>;
  logos?: Array<LogoDetection>;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class UtilsApiService {
  private readonly http = inject(HttpClient);

  getTimezones(): Observable<Array<NomenclatureItem>> {
    return this.http.get<Array<NomenclatureItem>>(UTILS_GET_TIMEZONES);
  }

  getLanguages(supportedOnly: boolean = true): Observable<Array<any>> {
    const httpParams = new HttpParams().set('supportedOnly', supportedOnly);
    return this.http.get<Array<any>>(UTILS_GET_LANGUAGES, {
      params: httpParams,
    });
  }

  getScheduleCoveragePeriods(): Observable<Array<any>> {
    return this.http.get<Array<any>>(UTILS_GET_COVER_PERIODS);
  }

  getCompanyProfitCenters(): Observable<Array<any>> {
    return this.http.get<Array<any>>(UTILS_GET_PROFIT_CENTERS);
  }

  getCompanyPartnerAgencies(): Observable<Array<any>> {
    return this.http.get<Array<any>>(UTILS_GET_PARTNER_AGENCIES);
  }

  getCountries(): Observable<Array<any>> {
    return this.http.get<Array<any>>(UTILS_GET_COUNTRIES);
  }

  getCitiesByCountryId(countryId: number): Observable<Array<any>> {
    const params = toQueryParams({ country_id: countryId || undefined });
    return this.http.get<Array<any>>(UTILS_GET_CITIES, { params });
  }

  getCustomAutoStoryPromptParameters(
    promptId: string,
  ): Observable<PromptParametersSchema> {
    return this.http.get<PromptParametersSchema>(UTILS_GET_SCHEMA, {
      params: { service: 'prompt', id: promptId },
    });
  }

  getAlgorithFormParams(algorithmId: number) {
    const httpParams = { params: { algorithmId } };
    return this.http.get<FormProperty[]>(
      UTILS_GET_ALGORITHM_PARAMETERS,
      httpParams,
    );
  }

  /** Retrieves the faces detected in an image. */
  getImageFaces(
    imageUrl: string,
    postId: string,
  ): Observable<ImageFacesResponse> {
    const normalizedImageUrl = this.normalizeImageUrl(imageUrl);
    const httpParams = toQueryParams({
      image_url: normalizedImageUrl,
      post_id: postId,
    });

    return this.http.get<ImageFacesResponse>(UTILS_GET_GET_IMAGE_FACES, {
      params: httpParams,
    });
  }

  /** Retrieves suggested image faces based on the provided query. */
  getImageFacesSuggest(query: string): Observable<Array<NomItem>> {
    const httpParams = toQueryParams({ query });

    return this.http.get<Array<NomItem>>(UTILS_GET_GET_IMAGE_FACES_SUGGEST, {
      params: httpParams,
    });
  }

  getTranslationTable(): Observable<TranslationTableRecord[]> {
    return this.http.get<TranslationTableRecord[]>(UTILS_GET_I18N_TABLE);
  }

  getTranslation(lang: string): Observable<any> {
    return this.http.get(UTILS_GET_I18N(lang));
  }

  /**
   * Registers a translation key the runtime could not resolve; `source` is the
   * current SPA route, stored as provenance for the unused-key audit.
   */
  reportMissingTranslation(
    keyword: string,
    source: string,
  ): Observable<unknown> {
    // URLSearchParams, not toFormBody: it %-encodes '+' in free-text values;
    // HttpParams' codec leaves '+' literal and PHP would read it as a space.
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('keyword', keyword);
    urlEncodedData.append('source', source);

    return this.http.post(UTILS_POST_I18N, urlEncodedData, {
      headers: HEADERS.formUrlEncoded,
    });
  }

  /** Assigns a face to a post image. */
  assignFace(
    postId: string,
    imageUrl: string,
    face: any,
  ): Observable<ImageFacesResponse> {
    const formData = {
      post_id: postId,
      image_url: this.normalizeImageUrl(imageUrl),
      face: face,
    };

    return this.http.post<ImageFacesResponse>(UTILS_POST_ASSIGN_FACE, formData);
  }

  /** Assigns a brand name to a detected logo, storing it as ground-truth. */
  assignLogo(
    imageUrl: string,
    logo: {
      brand: string;
      embedding: number[];
    },
  ): Observable<{ success: boolean; doc_id: string; brand: string }> {
    const body = {
      image_url: this.normalizeImageUrl(imageUrl),
      logo: logo,
    };

    return this.http.post<{ success: boolean; doc_id: string; brand: string }>(
      UTILS_POST_ASSIGN_LOGO,
      body,
    );
  }

  /** Unwraps proxied image URLs (`...url=<encoded>`) to the original source URL. */
  private normalizeImageUrl(imageUrl: string): string {
    if (!imageUrl) {
      return imageUrl;
    }

    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }

    const urlParamIndex = imageUrl.indexOf('url=');
    if (urlParamIndex === -1) {
      return imageUrl;
    }

    const rawParam = imageUrl.slice(urlParamIndex + 4);
    const paramValue = rawParam.split('&')[0];

    let decoded = paramValue;
    for (let i = 0; i < 2; i += 1) {
      try {
        decoded = decodeURIComponent(decoded);
      } catch {
        break;
      }
    }

    return decoded.startsWith('http://') || decoded.startsWith('https://')
      ? decoded
      : imageUrl;
  }

  translate(key: string, lang: string, value: string) {
    // URLSearchParams, not toFormBody: it %-encodes '+' in free-text values;
    // HttpParams' codec leaves '+' literal and PHP would read it as a space.
    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('key', key);
    urlEncodedData.append('lang', lang);
    urlEncodedData.append('value', value);

    return this.http.post(UTILS_POST_TRANSLATE, urlEncodedData, {
      headers: HEADERS.formUrlEncoded,
    });
  }
}
